import { observer } from 'mobx-react-lite'
import { ArrowPathIcon } from '@heroicons/react/20/solid'
import { useInjection } from '@core/providers/inversify-react.provider'
import { Button } from '@ui-kit/button.component'
import { Card, CardBody, Page, PageHeader } from '@ui-kit/page.component'
import { LoadError, SkeletonRows } from '@ui-kit/state.component'
import { GamesPagePresenter } from './games-page.presenter'

/**
 * Landing page for a signed-in account: every game it holds a seat in
 * (open and finished), plus the entry point into the lobby for a new or
 * joined one.
 */
export const GamesPageComponent = observer(() => {
  const presenter: GamesPagePresenter = useInjection(GamesPagePresenter)
  const vm = presenter.viewModel

  return (
    <Page>
      <PageHeader
        title={vm.title}
        actions={
          <div className="flex gap-2">
            <Button icon={ArrowPathIcon} onClick={() => presenter.load()} disabled={vm.isLoading}>
              {vm.reloadLabel}
            </Button>
            <Button tone="primary" onClick={() => presenter.goToLobby()}>
              {vm.newGameLabel}
            </Button>
          </div>
        }
      />

      <Card>
        <CardBody>
          {vm.showLoadError && (
            <LoadError message={vm.loadErrorMessage} retryLabel={vm.reloadLabel} onRetry={() => presenter.load()} />
          )}

          {vm.isLoading && <SkeletonRows label={vm.loadingLabel} />}

          {vm.showEmpty && <p className="text-sm text-shell-ink-secondary">{vm.emptyLabel}</p>}

          {!vm.isLoading && !vm.showLoadError && vm.rows.length > 0 && (
            <ul className="divide-y divide-shell-border">
              {vm.rows.map((row) => (
                <li key={row.key}>
                  <button
                    type="button"
                    onClick={() => presenter.openGame(row)}
                    className="flex w-full items-center justify-between py-3 text-left hover:bg-shell-surface-muted"
                  >
                    <span className="text-sm font-medium text-shell-ink">{row.label}</span>
                    <span
                      className={`text-sm ${row.isYourTurn ? 'font-semibold text-shell-accent' : 'text-shell-ink-secondary'}`}
                    >
                      {row.statusLabel}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </Page>
  )
})
